import { AppPaths } from "@p0security/shared/routes/constants";
import { ConfigProvider, Flex, Layout, Menu } from "antd";
import { ThemeConfig } from "antd/lib";
import React, { useCallback, useContext, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { spacing } from "styles/spacing";
import { colors } from "styles/variables";

import { useIsPrint } from "../../../hooks/useIsPrint";
import { EnvironmentDocsContext } from "../../Environment/contexts/EnvironmentDocsContext";
import { UserAuthz } from "../../Login";
import { useUser } from "../../Login/hook";
import { LoggedInUserNavItem } from "../LoggedInUserNavItem";
import { newBottomItems, newSidebarItems } from "./NavbarItems";
import { P0NavIcon } from "./P0NavIcon";

const { Sider } = Layout;

const navMenuTheme: ThemeConfig = {
  components: {
    Menu: {
      itemBg: "transparent",
      itemColor: colors.neutral["50"],
      itemHoverBg: colors.neutral["10"],
      itemHoverColor: colors.neutral["70"],
      itemSelectedBg: colors.tagColors.blue.bg,
      itemSelectedColor: colors.neutral["70"],
      itemBorderRadius: spacing.radius.sm,
      itemMarginBlock: spacing.scale.xs,
      itemMarginInline: spacing.scale.sm,
      activeBarBorderWidth: 0,
      dangerItemColor: colors.tagColors.red.text,
      dangerItemHoverColor: colors.tagColors.red.text,
      dangerItemActiveBg: colors.tagColors.red.bg,
      dangerItemSelectedColor: colors.tagColors.red.text,
    },
  },
};

export const Navbar: React.FC = () => {
  const userAuthz = useContext(UserAuthz);
  const isPrint = useIsPrint();
  const { tenant, signOut } = useUser();
  const { orgSlug } = useParams();
  const { pathname } = useLocation();
  const { hasEnvironments } = useContext(EnvironmentDocsContext);
  const [collapsed, setCollapsed] = useState(false);
  const toggleCollapsed = useCallback(() => setCollapsed((c) => !c), []);
  const handleStripHoverEnter = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.currentTarget.style.background = colors.neutral["10"];
    },
    []
  );
  const handleStripHoverLeave = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.currentTarget.style.background = "transparent";
    },
    []
  );

  const selectedNavKeys = useMemo(() => {
    const pathSegments = pathname.split("/");
    const appPath = pathSegments[3];
    return Object.values(AppPaths).filter((p) => p === appPath);
  }, [pathname]);

  if (isPrint) return null;

  const menuStyle = { background: "transparent", borderInlineEnd: "none" };

  return (
    <Sider
      width={248}
      collapsedWidth={72}
      collapsed={collapsed}
      onCollapse={setCollapsed}
      breakpoint="lg"
      theme="light"
      trigger={null}
      style={{
        background: colors.neutral["00"],
        position: "relative",
      }}
    >
      <ConfigProvider theme={navMenuTheme}>
        <Flex vertical style={{ height: "100%" }}>
          <Link
            to={`/o/${orgSlug}`}
            style={{
              padding: collapsed ? "16px 0" : "16px 28px",
              display: "flex",
              justifyContent: collapsed ? "center" : "flex-start",
            }}
          >
            <P0NavIcon collapsed={collapsed} />
          </Link>
          <Flex flex={1} vertical style={{ overflow: "auto" }}>
            <Menu
              theme="light"
              mode="inline"
              inlineCollapsed={collapsed}
              selectedKeys={selectedNavKeys}
              style={menuStyle}
              items={newSidebarItems(
                userAuthz,
                tenant.state === "found" && tenant.isSandbox,
                hasEnvironments
              )}
            />
          </Flex>
          <Flex
            vertical
            gap={spacing.scale.sm}
            style={{ paddingTop: spacing.scale.sm }}
          >
            <LoggedInUserNavItem collapsed={collapsed} />
            <Menu
              mode="inline"
              inlineCollapsed={collapsed}
              theme="light"
              selectable={false}
              style={menuStyle}
              items={newBottomItems(signOut)}
            />
          </Flex>
        </Flex>
      </ConfigProvider>
      <div
        role="button"
        aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
        title={collapsed ? "Expand" : "Collapse"}
        onClick={toggleCollapsed}
        className="p0-nav-collapse-strip"
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          width: 4,
          height: "100%",
          cursor: "ew-resize",
          background: "transparent",
          transition: "background 120ms ease",
        }}
        onMouseEnter={handleStripHoverEnter}
        onMouseLeave={handleStripHoverLeave}
      />
    </Sider>
  );
};
